import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { paths } from 'src/config/paths'
import { VerificationBadge } from 'src/shared/components/icons/verification-badge'
import { BottomSheet } from 'src/shared/components/ui/bottom-sheet'
import { Button } from 'src/shared/components/ui/buttons/button'
import { Dialog, DialogContent } from 'src/shared/components/ui/popups/dialog'
import { cn } from 'src/shared/utils/cn'

import { useUserByUsername } from '../../api/accounts/use-user-by-username'
import { usePublicLicenses } from '../../api/licenses/use-public-licenses'
import { useUserReviews } from '../../api/marketplace/use-user-reviews'
import { ExpertiseIcon } from '../../assets/general-info/expertise'
import { LanguagesIcon } from '../../assets/general-info/languages'
import { VerifiedLicensesIcon } from '../../assets/general-info/licenses'
import { WorksAtIcon } from '../../assets/general-info/works-at'
import type { Expertise } from '../../schemas/expertise'
import { expertiseMap } from '../../schemas/expertise'
import type { Language } from '../../schemas/language'
import { languageMap } from '../../schemas/language'
import { professionMap } from '../../schemas/profession'
import type { Profession } from '../../schemas/profession'
import { statesMap } from '../../schemas/states'
import type { State } from '../../schemas/states'
import { useMobilePublicProfileStore } from '../../stores/mobile-public-profile-store'
import { CoverPhotoFallback } from '../profile-photos/cover-photo'

import GeneralInfoSection from './general-info-section'
import OtherInfoContainer from './other-info-container'
import RequestCallButton from './ui/request-call-btn'

const Tab = ({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) => {
  return (
    <div
      className={cn(
        'flex flex-1 cursor-pointer items-center justify-center bg-white pb-[14px] pt-1 transition-all duration-200 ease-in-out sm:pt-2',
        active && 'border-b-primary',
      )}
      onClick={onClick}
      style={
        {
          position: 'relative',
          '--slide-progress': active ? '1' : '0',
        } as React.CSSProperties
      }
    >
      <p
        className={cn(
          'text-sm font-semibold leading-[1] text-muted transition-all duration-200 ease-in-out sm:text-base',
          active && 'font-bold text-primary',
        )}
      >
        {label}
      </p>
      {/* the underline for the tab regardless if active or not */}
      <div
        className="absolute bottom-[-1.5px] left-0 h-[1.5px] w-full rounded-full bg-[#efefef] transition-transform duration-300 ease-out"
        style={{
          transformOrigin: 'left',
        }}
      />
      {/* the underline for the tab if active */}
      <div
        className="absolute bottom-[-1.5px] left-0 h-[1.5px] w-full rounded-full bg-primary transition-transform duration-300 ease-out"
        style={{
          transform: `scaleX(${active ? 1 : 0})`,
          transformOrigin: 'left',
        }}
      />
    </div>
  )
}

const FIXED_MOBILE_HEADER_HEIGHT = 70

const PublicProfileMobileHeader = () => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const {
    activeTab,
    setActiveTab,
    setDividerScrollY,
    setIsDividerScrolledPast,
    setIsTabsScrolledPast,
    isTabsScrolledPast,
  } = useMobilePublicProfileStore()
  const [tabsTopPosition, setTabsTopPosition] = useState<number | null>(null)
  const { username } = useParams<{ username: string }>()
  const { data: user } = useUserByUsername(username || '')
  const [isBioModalOpen, setIsBioModalOpen] = useState(false)
  const [showEnlargedPhoto, setShowEnlargedPhoto] = useState(false)
  const { data: reviewData } = useUserReviews(user?.id || -1)
  const { data: publicUserLicenses } = usePublicLicenses(user?.id || -1)
  const reviewsCount = reviewData?.totalReviews ?? 0
  const averageRating = reviewData?.averageRating ?? 0

  const onSendInquiry = () => {
    if (!user?.id) return
    navigate(paths.marketplace.instantMatch.userId.getHref(user.id.toString()))
  }

  const setActiveTabAndURL = (tab: string) => {
    navigate(paths.users.publicProfile[tab].getHref(username as string), {
      replace: true,
      preventScrollReset: true,
    })
  }

  useEffect(() => {
    if (pathname.includes('listings')) {
      setActiveTab('listings')
    } else if (pathname.includes('reviews')) {
      setActiveTab('reviews')
    } else if (pathname.includes('portfolio')) {
      setActiveTab('portfolio')
    } else if (pathname.includes('activity')) {
      setActiveTab('activity')
    } else {
      setActiveTab('reviews')
    }
  }, [pathname, setActiveTab])

  // Track when divider becomes invisible due to fixed header
  useEffect(() => {
    const handleScroll = () => {
      const divider = document.getElementById('mobile-public-profile-profession')
      if (divider) {
        const rect = divider.getBoundingClientRect()

        // Check if divider is scrolled past (invisible due to fixed header)
        const isScrolledPast = rect.bottom <= FIXED_MOBILE_HEADER_HEIGHT

        // Set the boolean flag
        setIsDividerScrolledPast(isScrolledPast)

        // Store the scroll position only once when it first becomes invisible
        if (isScrolledPast && !divider.dataset.scrollY) {
          const scrollY = window.scrollY
          divider.dataset.scrollY = scrollY.toString()
          setDividerScrollY(scrollY)
        }
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [setDividerScrollY, setIsDividerScrolledPast])

  // Track when tabs become invisible due to fixed header
  useEffect(() => {
    const handleScroll = () => {
      const tabs = document.getElementById('mobile-public-profile-tabs')
      if (tabs) {
        const rect = tabs.getBoundingClientRect()

        // Check if tabs are scrolled past (when first pixel goes under header)
        const isScrolledPast = rect.top <= FIXED_MOBILE_HEADER_HEIGHT

        // Set the boolean flag
        setIsTabsScrolledPast(isScrolledPast)

        // Store the original position when tabs first become sticky
        // Only set if we don't already have a position or if tabs are visible again
        if (isScrolledPast && (tabsTopPosition === null || rect.top > FIXED_MOBILE_HEADER_HEIGHT)) {
          setTabsTopPosition(rect.top)
        }
      }
    }

    // Initial check in case tabs are already scrolled past on mount
    const tabs = document.getElementById('mobile-public-profile-tabs')
    if (tabs && tabsTopPosition === null) {
      const rect = tabs.getBoundingClientRect()
      if (rect.top <= FIXED_MOBILE_HEADER_HEIGHT) {
        setTabsTopPosition(rect.top)
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [setIsTabsScrolledPast, tabsTopPosition])

  const isLoanOfficer = user?.profession === 'mortgage_loan_officer'

  if (!user) {
    return null
  }

  return (
    <>
      <div className="fixed bottom-4 z-[11] mx-auto mt-2 flex w-full flex-row items-center gap-3 px-5">
        <div
          className="flex h-[60px] w-full flex-row items-center justify-between gap-3 rounded-full bg-white p-2"
          style={{
            boxShadow: '0px 0px 30px 2px rgba(27, 48, 41, 0.07)',
          }}
        >
          <RequestCallButton
            username={username as string}
            className="h-[45px] px-6"
            textClassName="text-[3.45vw] min-[450px]:text-[13px]"
          />
          <Button
            variant="outline"
            size="default"
            className="min-h-8 gap-2 rounded-full border-none bg-white px-3 md:min-h-12 md:gap-3"
            onClick={onSendInquiry}
          >
            <p className="text-[3.45vw] font-bold leading-none min-[450px]:text-[13px] md:text-base">Send inquiry</p>
            <svg xmlns="http://www.w3.org/2000/svg" width={14} height={14} viewBox="0 0 16 13" fill="none">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M8.38784 0.326996C8.73396 -0.0700158 9.33644 -0.111279 9.73342 0.234832L15.5678 5.32117C15.7755 5.50224 15.8947 5.76444 15.8947 6.03999C15.8947 6.31567 15.7755 6.57774 15.5678 6.75882L9.73342 11.8451C9.33644 12.1913 8.73396 12.1501 8.38784 11.7531C8.04172 11.356 8.08304 10.7536 8.48003 10.4075L12.3959 6.99368H0.953684C0.426983 6.99368 0 6.56668 0 6.03999C0 5.51331 0.426983 5.08631 0.953684 5.08631H12.3959L8.48003 1.67255C8.08304 1.32644 8.04172 0.724009 8.38784 0.326996Z"
                fill="#1B3029"
              />
            </svg>
          </Button>
        </div>
      </div>
      <div className="flex w-full flex-col items-start gap-4 bg-white">
        <img src={user.coverPhoto || CoverPhotoFallback} alt="cover photo" className="h-[160px] w-full object-cover" />
        <div className="mb-[-50px] flex w-full translate-y-[-50px] flex-col rounded-[40px] bg-white">
          <div className="mb-[-20px] flex translate-y-[-50px] flex-col gap-2">
            <div className="flex flex-col items-center gap-2 px-5 lg:px-8">
              <div
                onClick={() => setShowEnlargedPhoto(true)}
                role="button"
                style={{ boxShadow: '0px 2px 12px 1px rgba(27, 48, 41, 0.10)' }}
                className="mb-1 size-[100px] cursor-pointer rounded-full border-[3px] border-white bg-white lg:mb-3"
              >
                <img
                  src={user.profilePhoto}
                  alt="profile picture"
                  className="size-full rounded-full bg-white object-cover"
                />
              </div>
              <div className="flex flex-col items-center">
                <div className="flex items-center gap-2">
                  <h1 className={'text-[24px] font-bold lg:text-[28px]'}>{user.fullName}</h1>
                  <VerificationBadge isVerified={user.verified || false} className="mt-0.5" size={20} />
                </div>
                <div className="flex items-center gap-2" id="mobile-public-profile-profession">
                  <p className="text-sm text-primary lg:text-lg">{professionMap[user.profession].label}</p>
                </div>
              </div>
              {!user.biography ? (
                <div className="relative w-full items-center text-center text-sm italic text-muted lg:text-base">
                  <p className="line-clamp-1">{user.fullName} doesn&apos;t have a bio yet.</p>
                </div>
              ) : (
                <div className="relative w-full items-center text-center text-sm lg:text-base">
                  <p className="line-clamp-2 text-muted">{user.biography}</p>
                  <button onClick={() => setIsBioModalOpen(true)} className="-mt-2 text-primary hover:underline">
                    Read full bio
                  </button>
                </div>
              )}
              <OtherInfoContainer
                isCurrentUser={false}
                reviewsCount={reviewsCount}
                averageRating={averageRating}
                shortAddress={user.shortAddress || ''}
              />
              <div className="my-4 h-px w-full bg-[#efefef] lg:my-7" />
            </div>
            <div className="flex flex-col gap-5 px-5 lg:gap-6 lg:px-8">
              <GeneralInfoSection
                icon={<VerifiedLicensesIcon className="size-7 text-primary lg:size-8" />}
                title="Verified Licenses"
                subtitle={isLoanOfficer ? publicUserLicenses?.[0].licenseNumber || undefined : undefined}
                text={publicUserLicenses
                  ?.map((license) =>
                    isLoanOfficer
                      ? `${statesMap[license.state as unknown as State]?.label}`
                      : `${statesMap[license.state as unknown as State]?.label} (#${license.licenseNumber})`,
                  )
                  .join(' · ')}
              />
              {user?.companyName && (
                <GeneralInfoSection
                  icon={<WorksAtIcon className="size-7 text-primary lg:size-8" />}
                  title="Works at"
                  text={user?.companyName || 'N/A'}
                />
              )}
              {user?.expertise && user.expertise?.length > 0 && (
                <GeneralInfoSection
                  icon={<ExpertiseIcon className="size-7 text-primary lg:size-8" />}
                  title="Expertise"
                  text={user?.expertise
                    ?.map(
                      (e) => expertiseMap[user?.profession as unknown as Profession][e as unknown as Expertise]?.label,
                    )
                    .join(' · ')}
                />
              )}
              <GeneralInfoSection
                icon={<LanguagesIcon className="size-7 text-primary lg:size-8" />}
                title="Languages"
                text={user?.languages?.map((e) => languageMap[e as unknown as Language]?.label).join(' · ')}
              />
            </div>
          </div>
          <div id="mobile-public-profile-tabs" className="-mb-2 flex flex-row items-start px-5 pt-3 md:mb-4">
            {/* <Tab label="Listings" active={activeTab === 'listings'} onClick={() => setActiveTabAndURL('listings')} /> */}
            <Tab label="Reviews" active={activeTab === 'reviews'} onClick={() => setActiveTabAndURL('reviews')} />
            <Tab label="Portfolio" active={activeTab === 'portfolio'} onClick={() => setActiveTabAndURL('portfolio')} />
            <Tab label="Activity" active={activeTab === 'activity'} onClick={() => setActiveTabAndURL('activity')} />
          </div>
        </div>
      </div>
      <BottomSheet open={isBioModalOpen} close={() => setIsBioModalOpen(false)}>
        <div className="flex flex-col gap-4">
          <h1 className="text-[26px] font-bold">About {user.firstName}</h1>
          <p className="text-[15px] text-muted md:text-base">{user.biography}</p>
        </div>
      </BottomSheet>

      {/* Sticky Tabs - positioned exactly where original tabs are */}
      <div
        className={cn(
          'fixed left-0 right-0 z-40 bg-white px-5 pt-3 shadow-sm transition-opacity duration-200',
          isTabsScrolledPast ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
        style={{
          top: isTabsScrolledPast ? tabsTopPosition ?? FIXED_MOBILE_HEADER_HEIGHT : 'auto',
        }}
      >
        <div className="flex flex-row items-start">
          <Tab label="Reviews" active={activeTab === 'reviews'} onClick={() => setActiveTabAndURL('reviews')} />
          <Tab label="Portfolio" active={activeTab === 'portfolio'} onClick={() => setActiveTabAndURL('portfolio')} />
          <Tab label="Activity" active={activeTab === 'activity'} onClick={() => setActiveTabAndURL('activity')} />
        </div>
      </div>

      {/* Profile Picture Modal */}
      <Dialog open={showEnlargedPhoto} onOpenChange={setShowEnlargedPhoto}>
        <DialogContent className="max-w-md border-none bg-transparent p-0 shadow-none" hideClose>
          <div className="flex justify-center">
            <img
              src={user.profilePhoto}
              alt="profile picture"
              className="size-[300px] rounded-full border-[3px] border-white bg-white object-cover"
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default PublicProfileMobileHeader
